import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  CircularProgress,
  IconButton,
  useTheme,
  Typography,
} from '@mui/material'
import FindInPageIcon from '@mui/icons-material/FindInPage'
import { fetchViewLink } from '../../api/customHooks/fetchViewLink'

interface PandaDocViewerProps {
  contractId: any
  customFileName?: string
  disabled?: boolean
  size?: { height: number; width: number }
}

const PandaDocViewer = ({ contractId, customFileName, disabled = false, size = { height: 30, width: 30 } }: PandaDocViewerProps) => {
  const [open, setOpen] = useState(false)
  const [blob, setBlob] = useState(null)
  const [loading, setLoading] = useState(false)
  const theme = useTheme()

  const handleClickOpen = async () => {
    setLoading(true)
    const fetchedBlob = await fetchViewLink(contractId)
    if (fetchedBlob) setBlob(fetchedBlob)
    setLoading(false)
    setOpen(true)
  }

  const handleDownload = () => {
    if (blob) {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = customFileName || `contract_${contractId}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    }
  }

  const handleClose = () => {
    setOpen(false)
    setBlob(null)
  }

  return (
    <>
      <IconButton color="inherit" onClick={handleClickOpen} sx={{ ml: 2, zIndex: 20 }} disabled={disabled}>
        <FindInPageIcon
          sx={{
            color: disabled ? theme.palette.text.secondary : 'rgb(37, 133, 103)',
            height: size?.height,
            width: size?.width,
          }}
        />
      </IconButton>

      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography>{customFileName}</Typography>
          <Typography>Document Preview</Typography>
          {/* Custom Download Button */}
          <Button onClick={handleDownload} sx={{ float: 'right' }} disabled={loading || !blob}>
            Download
          </Button>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <CircularProgress />
          ) : blob ? (
            // Replace iframe with object for better display control
            <object data={window.URL.createObjectURL(blob)} type="application/pdf" width="100%" height="800px">
              <p>
                Your browser does not support PDFs.{' '}
                <a href={window.URL.createObjectURL(blob)} target="_blank" rel="noreferrer">
                  Click here to download the PDF
                </a>
              </p>
            </object>
          ) : (
            <p>Error loading document.</p>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

export default PandaDocViewer
/*
import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  CircularProgress,
  IconButton,
  useTheme,
  Typography,
} from '@mui/material'
import FindInPageIcon from '@mui/icons-material/FindInPage'
import { useViewLink } from '../../api/aws/panda/useViewLink'

const PandaDocViewer = ({ contractId, customFileName, height, width, disabled }) => {
  const [open, setOpen] = useState(false)
  const [blobUrl, setBlobUrl] = useState(null)
  const theme = useTheme()

  const { data: blob, isFetching, refetch } = useViewLink(contractId, open)

  useEffect(() => {
    if (blob) {
      const url = URL.createObjectURL(blob)
      setBlobUrl(url)
      return () => URL.revokeObjectURL(url)
    }
  }, [blob])

  const handleClickOpen = () => {
    setOpen(true)
    refetch() // Trigger the query when dialog opens
  }

  const handleDownload = () => {
    if (blobUrl) {
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = customFileName || `contract_${contractId}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(blobUrl)
    }
  }

  const handleClose = () => {
    setOpen(false)
    setBlobUrl(null)
  }

  return (
    <>
      <IconButton color='inherit' onClick={handleClickOpen} sx={{ ml: 2, zIndex: 20 }} disabled={disabled}>
        <FindInPageIcon
          sx={{
            color: disabled ? theme.palette.text.secondary : 'rgb(37, 133, 103)',
            height: 30,
            width: 30,
          }}
        />
      </IconButton>

      <Dialog open={open} onClose={handleClose} maxWidth='lg' fullWidth>
        <DialogTitle
          sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography>{customFileName}</Typography>
          <Typography>Document Preview</Typography>
          <Button onClick={handleDownload} sx={{ float: 'right' }} disabled={isFetching || !blobUrl}>
            Download
          </Button>
        </DialogTitle>
        <DialogContent>
          {isFetching ? (
            <CircularProgress />
          ) : blobUrl ? (
            <object data={blobUrl} type='application/pdf' width='100%' height='800px'>
              <p>
                Your browser does not support PDFs.{' '}
                <a href={blobUrl} target='_blank' rel='noreferrer'>
                  Click here to download the PDF
                </a>
              </p>
            </object>
          ) : (
            <Typography color='error'>Error loading document.</Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

export default PandaDocViewer


*/
